import { getAuthToken, validateAuthToken } from "@/services/auth.service";
import { useGlobalStore } from "@/stores/globalStore";
import { errorHandler } from "@/utils/errorHandler";
import { UnauthorizedError } from "@/utils/errors";
import { createRouter, createWebHistory } from "vue-router";

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: "/",
      name: "home",
      component: () => import("@/views/General/HomeView.vue"),
      meta: { requiresAuth: true, darkBackground: true },
    },
    {
      path: "/athlete-folder/:id",
      name: "athletFolder",
      component: () =>
        import("@/views/Feature/FilesManagement/AthleteEnveloppeView.vue"),
      meta: { requiresAuth: true, role: ["validator", "dashboard"] },
    },
    {
      path: "/athlete-status",
      name: "athleteStatus",
      component: () =>
        import(
          "@/views/Feature/Administrator/information/AthleteStatusView.vue"
        ),
      meta: { requiresAuth: true, role: ["admin", "dashboard"] },
    },
    {
      path: "/auth",
      name: "auth",
      component: () => import("@/views/General/HomeView.vue"),
      meta: { requiresAuth: false, darkBackground: false },
    },
    {
      path: "/calendar",
      name: "calendar",
      component: () =>
        import("@/views/Feature/Administrator/information/CalendarView.vue"),
      meta: { requiresAuth: true, role: ["admin", "dashboard"], darkBackground: true  },
    },
    {
      path: "/dashboard",
      name: "dashboard",
      component: () =>
        import("@/views/Feature/Administrator/information/StatsView.vue"),
      meta: { requiresAuth: true, role: ["dashboard"], darkBackground: true },
    },
    {
      path: "/editor",
      name: "editor",
      component: () => import("@/views/Feature/Editor/EditorView.vue"),
      meta: { requiresAuth: true, role: ["editor"], darkBackground: true },
    },
    {
      path: "/file-upload",
      name: "file-upload",
      component: () =>
        import(
          "@/views/Feature/Administrator/actions/FileUploadView.vue"
        ),
      meta: { requiresAuth: true, role: ["admin","photographer"] },
    },
    {
      path: "/files-list/:folder",
      name: "filesList",
      component: () =>
        import("@/views/Feature/FilesManagement/FilesListView.vue"),
      meta: { requiresAuth: true, role: ["validator"] },
    },
    {
      path: "/folder-list/:folder",
      name: "folderList",
      component: () =>
        import("@/views/Feature/FilesManagement/FilesListView.vue"),
      meta: { requiresAuth: true, role: ["validator", "dashboard"] },
    },
    {
      path: "/files-menu",
      name: "files-menu",
      component: () =>
        import("@/views/Feature/FilesManagement/FilesMenuView.vue"),
      meta: { requiresAuth: true, role: ["validator"] },
    },
    {
      path: "/last-actions",
      name: "lastActions",
      component: () =>
        import(
          "@/views/Feature/Administrator/information/AllUsersActionsListView.vue"
        ),
      meta: { requiresAuth: true, role: ["admin"] },
    },

    {
      path: "/user-actions",
      name: "userActions",
      component: () =>
        import(
          "@/views/Feature/Administrator/information/UserActionListView.vue"
        ),
      meta: { requiresAuth: true, role: ["admin"] },
    },
    {
      path: "/reference-file-upload",
      name: "referenceFileUpload",
      component: () =>
        import(
          "@/views/Feature/Administrator/actions/ReferenceFileUploadView.vue"
        ),
      meta: { requiresAuth: true, role: ["weaverize"] },
    },
    {
      path: "/role-manager",
      name: "role-manager",
      component: () =>
        import("@/views/Feature/Administrator/management/RoleManagerView.vue"),
      meta: { requiresAuth: true, role: ["admin"], darkBackground: true },
    },
    {
      path: "/quality-control",
      name: "quality-control",
      component: () =>
        import("@/views/Feature/QualityControl/QualityControlView.vue"),
      meta: { requiresAuth: true, role: ["validator"], darkBackground: false },
    },
    {
      path: "/stats",
      name: "stats",
      component: () =>
        import("@/views/Feature/Administrator/information/StatsView.vue"),
      meta: { requiresAuth: true, role: ["admin"], darkBackground: true },
    },
    {
      path: "/site-manager",
      name: "site-manager",
      component: () =>
        import("@/views/Feature/Administrator/management/SiteManagerView.vue"),
      meta: { requiresAuth: true, role: ["admin"], darkBackground: true },
    },
    {
      path: "/kit-manager",
      name: "kit-manager",
      component: () =>
        import("@/views/Feature/Administrator/management/KitManagerView.vue"),
      meta: { requiresAuth: true, role: ["admin"], darkBackground: true },
    },
    {
      path: "/cluster-manager",
      name: "cluster-manager",
      component: () =>
        import(
          "@/views/Feature/Administrator/management/ClusterManagerView.vue"
        ),
      meta: { requiresAuth: true, role: ["admin"], darkBackground: true },
    },
    {
      path: "/photographer-manager",
      name: "photographer-manager",
      component: () =>
        import(
          "@/views/Feature/Administrator/management/PhotographerManagerView.vue"
        ),
      meta: { requiresAuth: true, role: ["admin"], darkBackground: true },
    },

    {
      path: "/unauthorized",
      name: "unauthorized",
      component: () => import("@/views/General/UnauthorizedView.vue"),
      meta: { requiresAuth: false },
    },
    {
      path: "/:pathMatch(.*)*",
      name: "not-found",
      component: () => import("@/views/General/NotFoundView.vue"),
      meta: { requiresAuth: false },
    },
  ],
});

router.beforeEach(async (to, from, next) => {
  const globalStore = useGlobalStore();

  resetTooltip(globalStore);

  if (to.name === "auth" && to.query.token) {
    const token = to.query.token as string;
    if (token) localStorage.setItem("authToken", token);
    const isMobile =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
    globalStore.chatMenuOpen = isMobile;
    globalStore.isMobile = isMobile;
    return next({ name: "home" });
  }

  const token = to.query.token ?? getAuthToken();
  globalStore.hasToken = !!token;

  if (!to.meta.requiresAuth) {
    return next();
  }

  if (!token) {
    return next({ name: "unauthorized" });
  }

  localStorage.setItem("authToken", token as string);

  try {
    const res = await validateAuthToken();

    if (!res || !res.roles) {
      resetUserState(globalStore);
      globalStore.hasToken = false;

      localStorage.removeItem("authToken");
      return next({ name: "unauthorized" });
    }

    setUserState(globalStore, res);
    let targetRoute = "home";

    if (!globalStore.hasLoggedInBefore) {
      const roles = globalStore.userRoles;

      if (roles.includes("admin") || roles.includes("weaverize")) {
        targetRoute = "stats";
      } else if (roles.includes("dashboard") && !roles.includes("admin")) {
        targetRoute = "dashboard";
      } else if (roles.includes("editor")) {
        targetRoute = "editor";
      } else if (roles.includes("photographer")) {
        targetRoute = "file-upload";
      } else if (roles.includes("validator")) {
        targetRoute = "quality-control";
      } else {
          targetRoute = 'unauthorized';
      }
    }

    if (["home", "/"].includes(to.name as string) || to.path === "/") {
      const roles = globalStore.userRoles;      
      if (roles.includes("admin") || roles.includes("weaverize")) {
        if (to.name !== "stats") return next({ name: "stats" });
      } else if (roles.includes("dashboard") && !roles.includes("admin")) {
        if (to.name !== "dashboard") return next({ name: "dashboard" });
      } else if (roles.includes("editor")) {
        if (to.name !== "editor") return next({ name: "editor" });
      } else if (roles.includes("photographer")) {
        if (to.name !== "file-upload") return next({ name: "file-upload" });
      } else if (roles.includes("validator")) {
        if (to.name !== "quality-control")
          return next({ name: "quality-control" });
      } else {
        return next({ name: "unauthorized" });
      }

      // Ne rediriger que si on n'est pas déjà sur la route cible
      if (to.name !== targetRoute) {
        return next({ name: targetRoute });
      }
      // Si on est déjà sur la bonne route, continuer normalement
    }

    if (
      Array.isArray(to.meta.role) &&
      to.meta.role.length > 0 &&
      !globalStore.isAdmin
    ) {
      const hasAllowedRole = to.meta.role.some((r: string) =>
        globalStore.userRoles.includes(r)
      );
      if (!hasAllowedRole) {
        return next({ name: "unauthorized" });
      } else {
        
        return next();
      }
    }

    if (globalStore.userRoles.includes("dashboard") && !globalStore.isAdmin) {
      if (
        to.meta.requiresAuth &&
        to.name !== "dashboard" &&
        to.name !== "athleteStatus" &&
        to.path !== "/folder-list/COMPLETED" &&
        to.name !== "athletFolder"
      ) {
        return next({ name: "unauthorized" });
      } else {
        return next();
      }
    }

    return next();
  } catch (error) {
    if (error instanceof Error) {
      if (error.constructor === UnauthorizedError) {
        localStorage.removeItem("authToken");
      }
      errorHandler(error);
    }
    globalStore.hasToken = false;
    resetUserState(globalStore);
    return next({ name: "unauthorized" });
  }
});

function resetTooltip(store: ReturnType<typeof useGlobalStore>) {
  store.tooltip = false;
  store.tooltipText = "";
  store.tooltipPosition = null;
}

function resetUserState(store: ReturnType<typeof useGlobalStore>) {
  store.isAdmin = false;
  store.userRoles = [];
  store.userName = "";
  store.userId = "";
  store.userMail = "";
  store.hasLoggedInBefore = true;
  store.hasToken = false;
}

function setUserState(store: ReturnType<typeof useGlobalStore>, res: any) {
  const roles = res.roles ?? [];
  store.userRoles = roles;
  store.userName = res.username ?? "";
  store.userId = res.id ?? "";
  store.userMail = res.email ?? "";
  store.hasLoggedInBefore = res.hasLoggedInBefore ?? true;
  store.isAdmin = roles.includes("admin") ?? roles.includes("weaverize");
}

export default router;
